import version from 'constants/localStorageVersion'

const VERSION_KEY = '_indexedDBVersion'
const DB_NAME = 'FormBeatStorage'
const DB_VERSION = 1
const STORE_NAME = 'keyValueStore'

class IndexedDBService {
  constructor() {
    this.db = null
    this.dbPromise = this.initDB()
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        }
      }
    })
  }

  async getDB() {
    if (!this.db) {
      await this.dbPromise
    }
    return this.db
  }

  async persist(key, value) {
    const db = await this.getDB()
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.put({ key, value: JSON.stringify(value) })
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async load(key, defaultValue = undefined) {
    try {
      const db = await this.getDB()
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)

      return new Promise((resolve) => {
        const request = store.get(key)
        request.onsuccess = () => {
          if (request.result) {
            try {
              resolve(JSON.parse(request.result.value) || defaultValue)
            } catch (e) {
              resolve(defaultValue)
            }
          } else {
            resolve(defaultValue)
          }
        }
        request.onerror = () => resolve(defaultValue)
      })
    } catch (e) {
      return defaultValue
    }
  }

  async remove(key) {
    const db = await this.getDB()
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clear() {
    const db = await this.getDB()
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // Clean up localStorage to free space
  clearLocalStorage() {
    try {
      localStorage.clear()
    } catch (e) {
      // Ignore localStorage errors
    }
  }
}

const indexedDBService = new IndexedDBService()

const StorageService = {
  async init() {
    const currentVersion = await this.load(VERSION_KEY)
    if (currentVersion !== version) {
      await this.clear()
      // Clean up localStorage on version change
      indexedDBService.clearLocalStorage()
    }

    await this.persist(VERSION_KEY, version)
  },

  async persist(key, value) {
    return indexedDBService.persist(key, value)
  },

  async load(key, defaultValue = undefined) {
    return indexedDBService.load(key, defaultValue)
  },

  async remove(key) {
    return indexedDBService.remove(key)
  },

  async clear() {
    return indexedDBService.clear()
  }
}

// Initialize storage in background
StorageService.init().catch((err) => {
  console.warn('StorageService initialization failed:', err)
})

export default StorageService
